// Sugalarm — "update your CGM credentials" modal (cycle 88).
//
// Shown whenever the ACTIVE member's `cgmAuthFailed` (from the WS
// `cgm_auth` frame / connect-time snapshot) is true. The parent in the
// authenticated shell gates the mount on that flag, so when credentials
// are corrected on ANY device the cleared flag arrives over WS, the flag
// goes false, and this modal unmounts on every device at once —
// regardless of any local "Not now" dismiss.
//
// Two views: (1) the explanation + "Update credentials" / "Not now"
// actions; (2) the in-place update form (CGMConnectForm in update mode —
// no ProviderPicker, provider+region read-only, username prefilled,
// password blank). Submitting runs the existing active-member connect
// path, which re-encrypts, clears the flag, and broadcasts.
//
// Kiosk: centered when the on-screen keyboard is closed; when a field is
// focused (keyboard slides up) the panel anchors to the top and scrolls
// the focused field + submit into view above the keyboard, in both
// nav_position modes.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import * as cgmApi from "../../api/cgm";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { Button } from "../ui/Button";
import { CGMConnectForm } from "../../screens/login/CGMConnectForm";
import { findProvider } from "../../screens/login/providers";
import styles from "./CgmAuthModal.module.css";
import { overlayHost } from "../../lib/overlayHost";

interface Props {
  variant: FrameVariant;
  /** Active member's display name for the copy (optional). */
  memberName?: string | null;
  /** Cycle 453.2: an alarm episode is live. The alarm wins — the modal is
   *  hidden (kept mounted, so anything typed survives) until it ends. */
  stepAside?: boolean;
}

// Cycle 453.2 (operator decision 3): this modal mounts INSIDE the frame root
// (`lib/overlayHost.ts`), below the alarm layer (370), so the no-data banner —
// which is almost always up when a CGM login has been refused — stays on top
// of it. Mounted at the app root it covered every alarm control in the frame.
export function CgmAuthModal({ variant, memberName, stepAside = false }: Props) {
  const [dismissed, setDismissed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [status, setStatus] = useState<cgmApi.CGMStatus | null>(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Fetch status once for the prefill (provider / region / username).
  useEffect(() => {
    let alive = true;
    void cgmApi.status().then((r) => {
      if (alive && r.ok) setStatus(r.data);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Kiosk: track keyboard via focus within the panel, and scroll the
  // focused field into view above the on-screen keyboard.
  const isKiosk = variant === "kiosk";
  const onFocusCapture = (e: React.FocusEvent) => {
    if (!isKiosk) return;
    const t = e.target as HTMLElement;
    if (t.tagName === "INPUT" || t.tagName === "SELECT") {
      setKeyboardOpen(true);
      // Scroll the focused field's labelled group to the TOP of the panel
      // so the header scrolls off and the fields/submit BELOW it stay
      // visible above the keyboard (cycle 88.1: was `nearest`, which left
      // the header in place and pushed the submit off the bottom).
      const group = t.closest("label") ?? t;
      window.setTimeout(
        () => group.scrollIntoView({ block: "start", behavior: "smooth" }),
        250,
      );
    }
  };
  const onBlurCapture = (e: React.FocusEvent) => {
    if (!isKiosk) return;
    // Grace period so tabbing username→password doesn't flap the layout.
    const next = e.relatedTarget as HTMLElement | null;
    if (next && panelRef.current?.contains(next)) return;
    window.setTimeout(() => setKeyboardOpen(false), 100);
  };

  if (dismissed) return null;

  const provider = status?.provider ? findProvider(status.provider) : null;
  const initialRegion: "US" | "OUS" =
    status?.region === "OUS" ? "OUS" : "US";

  const modal = (
    <div
      hidden={stepAside}
      className={`${styles.backdrop} ${
        isKiosk && keyboardOpen ? styles.kbOpen : ""
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Update CGM credentials"
    >
      {!showForm || !provider ? (
        <div
          ref={panelRef}
          className={styles.panel}
          onFocusCapture={onFocusCapture}
          onBlurCapture={onBlurCapture}
        >
          <div className={styles.icon} aria-hidden>
            ⚠
          </div>
          <div className={styles.title}>CGM not updating</div>
          <p className={styles.body}>
            No data is coming from
            {memberName ? ` ${memberName}'s` : " your"} CGM. Please update
            your CGM provider's login credentials — this is usually caused by
            a recent password change.
          </p>
          <div className={styles.actions}>
            <Button
              full
              onClick={() => setShowForm(true)}
              disabled={status === null}
            >
              Update credentials
            </Button>
            <Button full variant="ghost" onClick={() => setDismissed(true)}>
              Not now
            </Button>
          </div>
        </div>
      ) : (
        // Form view: CGMConnectForm is its own card — wrap in the
        // transparent scroll container (no second card).
        <div
          ref={panelRef}
          className={styles.formWrap}
          onFocusCapture={onFocusCapture}
          onBlurCapture={onBlurCapture}
        >
          <CGMConnectForm
            provider={provider}
            mode="update"
            initialIdentifier={status?.username ?? ""}
            initialRegion={initialRegion}
            onChangeProvider={() => {}}
            onCancel={() => setShowForm(false)}
            onConnected={() => {
              // The successful connect cleared the flag + broadcast
              // `cgm_auth{auth_failed:false}`; the parent unmounts this
              // modal when `cgmAuthFailed` flips. Hide locally meanwhile.
              setShowForm(false);
              setDismissed(true);
            }}
          />
        </div>
      )}
    </div>
  );
  const host = overlayHost();
  return host ? createPortal(modal, host) : modal;
}
